import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        authAPI.me().then(res => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        }).catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const applySession = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const login = async (email, password, captcha = {}) => {
    const res = await authAPI.login({ email, password, ...captcha });
    // With 2FA, login returns an intermediate step (confirm_email / otp) with no token yet.
    if (res.data?.token) return applySession(res.data);
    return res.data;
  };

  const verifyOtp = async (pending_token, otp) => {
    const res = await authAPI.verifyOtp({ pending_token, otp });
    return applySession(res.data);
  };

  const register = async (name, email, password) => {
    const res = await authAPI.register({ name, email, password });
    if (res.data.pending) {
      return res.data;
    }
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const canEdit = user?.role === 'admin' || user?.role === 'quality_manager';
  const isAdmin = user?.role === 'admin';
  
  // Helper to check if user can edit metadata (add scopes of approval, etc.)
  // App Users can make transactional entries but NOT modify metadata
  const canEditMetadata = (appName) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'quality_manager') return true;
    // For per-app role, only "manager" can edit metadata, not "app_user"
    const appRole = user.app_roles?.[appName];
    return appRole === 'manager';
  };
  
  // Helper to check if user can make transactional entries (create/edit records)
  // Both "manager" and "app_user" can do this
  const canMakeEntries = (appName) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'quality_manager') return true;
    const appRole = user.app_roles?.[appName];
    return appRole === 'manager' || appRole === 'app_user';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      verifyOtp,
      register, 
      logout, 
      canEdit, 
      isAdmin,
      canEditMetadata,
      canMakeEntries
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
